import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ChevronDown, Layers, AlertCircle, RefreshCw, ArrowLeft, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/client";
import { getApiErrorMessage } from "../shared/utils/apiError";
import { GanttChart } from "../components/timeline/GanttChart";
import type { TimelineSection, ZoomLevel } from "../utils/timeline-geometry";
import { cn, cardClassName } from '@jira-clone/ui';

// A small fixed palette so each project gets a stable, distinguishable badge color
const PROJECT_COLORS = ["#0C66E4", "#7c3aed", "#B35C00", "#008DA6", "#C9372C", "#2F7D32", "#8B5CF6", "#0B6E4F"];

function colorForProject(projectId: string, orderedProjectIds: string[]): string {
  const idx = orderedProjectIds.indexOf(projectId);
  return PROJECT_COLORS[(idx < 0 ? 0 : idx) % PROJECT_COLORS.length];
}

interface PortfolioTimelineIssue {
  id: string;
  key: string;
  title: string;
  type: string;
  priority: string;
  assigneeId: string | null;
  sprintId: string | null;
  parentId: string | null;
  statusCategory: string;
  storyPoints: number | null;
  startDate: string | null;
  dueDate: string | null;
  projectId: string;
  projectKey: string;
}

interface PortfolioEpicTimelineGroup {
  epic: PortfolioTimelineIssue;
  effectiveStartDate: string | null;
  effectiveDueDate: string | null;
  startIsRollup: boolean;
  dueIsRollup: boolean;
  children: PortfolioTimelineIssue[];
}

interface TimelineDependency {
  blockerId: string;
  blockedId: string;
}

interface PortfolioTimelineResult {
  epics: PortfolioEpicTimelineGroup[];
  unassigned: PortfolioTimelineIssue[];
  dependencies: TimelineDependency[];
}

interface PortfolioTimelineResponse {
  timeline: PortfolioTimelineResult;
  projectIds: string[];
  accessibleProjectIds: string[];
}

interface ProjectMeta {
  id: string;
  _id?: string;
  name: string;
  key: string;
  color?: string;
}

interface OrganizationMeta {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
}

function buildPortfolioSections(data: PortfolioTimelineResult, orderedProjectIds: string[]): TimelineSection[] {
  function toRow(issue: PortfolioTimelineIssue, indent: boolean, startDate: string | null, dueDate: string | null, draggable: boolean) {
    const pid = issue.projectId || (issue as any).spaceId || "";
    const pkey = issue.projectKey || (issue as any).spaceKey || "PROJ";
    return {
      id: issue.id,
      key: issue.key,
      title: issue.title,
      type: issue.type,
      indent,
      startDate,
      dueDate,
      draggable,
      linkTo: `/spaces/${pid}/summary`,
      badge: { label: pkey, color: colorForProject(pid, orderedProjectIds) },
    };
  }

  const sections: TimelineSection[] = data.epics.map((g) => {
    const pkey = g.epic.projectKey || (g.epic as any).spaceKey || "PROJ";
    return {
      id: g.epic.id,
      label: `${pkey} / ${g.epic.key} — ${g.epic.title}`,
      rows: [
        toRow(g.epic, false, g.effectiveStartDate, g.effectiveDueDate, !g.startIsRollup && !g.dueIsRollup),
        ...g.children.map((c) => toRow(c, true, c.startDate, c.dueDate, true)),
      ],
    };
  });

  if (data.unassigned.length > 0) {
    // Group unassigned tasks by project when there are multiple projects
    const byProject = new Map<string, PortfolioTimelineIssue[]>();
    for (const item of data.unassigned) {
      const pid = item.projectId || (item as any).spaceId || "";
      const list = byProject.get(pid) || [];
      list.push(item);
      byProject.set(pid, list);
    }

    if (data.epics.length === 0 && byProject.size > 1) {
      for (const [projectId, issues] of Array.from(byProject.entries())) {
        const projectKey = issues[0]?.projectKey || (issues[0] as any)?.spaceKey || "Project";
        sections.push({
          id: `__project_${projectId}`,
          label: `${projectKey} (${issues.length} tasks)`,
          rows: issues.map((c: PortfolioTimelineIssue) => toRow(c, false, c.startDate, c.dueDate, true)),
        });
      }
    } else {
      sections.push({
        id: "__unassigned",
        label: data.epics.length > 0 ? "Other Tasks (No epic)" : "Project Tasks",
        rows: data.unassigned.map((c: PortfolioTimelineIssue) => toRow(c, false, c.startDate, c.dueDate, true)),
      });
    }
  }

  return sections;
}

/**
 * PortfolioTimelinePage
 * Ported from JIRA-ZYNX PortfolioTimelineView (prottime folder).
 * Unified cross-project roadmap view with epic rollups, date scales, and blockers.
 * Uses orgId from URL params (like prottime) and allows organization switching.
 */
export default function PortfolioTimelinePage() {
  const { orgId: paramOrgId } = useParams<{ orgId?: string }>();
  const navigate = useNavigate();
  const { user, organization: authOrg, refreshAuth } = useAuth();

  const [allProjects, setAllProjects] = useState<ProjectMeta[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<OrganizationMeta[]>([]);
  const [accessibleProjectIds, setAccessibleProjectIds] = useState<string[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[] | null>(null); // null = "all accessible"
  const [data, setData] = useState<PortfolioTimelineResult | null>(null);
  const [zoom, setZoom] = useState<ZoomLevel>("month");
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const [isTimelineLoading, setIsTimelineLoading] = useState(true);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const isLoading = isTimelineLoading || isProjectsLoading;
  const [error, setError] = useState<string | null>(null);
  const [currentOrg, setCurrentOrg] = useState<OrganizationMeta | null>(null);

  const orgId = paramOrgId || authOrg?.id || (authOrg as any)?._id || currentOrg?.id || allOrganizations[0]?.id;

  // Load all organizations for the org switcher
  useEffect(() => {
    api.get<any[]>('/organizations')
      .then((res) => {
        const rawList = Array.isArray(res) ? res : [];
        const orgs = rawList
          .map((m: any) => {
            const o = m?.organization || m;
            if (!o) return null;
            return {
              id: String(o._id || o.id),
              name: o.name || 'Organization',
              slug: o.slug || '',
              logoUrl: o.logoUrl,
            };
          })
          .filter(Boolean) as OrganizationMeta[];
        setAllOrganizations(orgs);
        const effectiveId = paramOrgId || authOrg?.id || (authOrg as any)?._id || orgs[0]?.id;
        const current = orgs.find((o) => o.id === effectiveId) || orgs[0] || null;
        if (current) setCurrentOrg(current);
      })
      .catch((err) => console.error("Failed to load organizations:", err));
  }, [paramOrgId, authOrg?.id, (authOrg as any)?._id]);

  // Keep URL in sync if accessed via /app/portfolio-timeline
  useEffect(() => {
    const resolvedId = paramOrgId || authOrg?.id || (authOrg as any)?._id || currentOrg?.id || allOrganizations[0]?.id;
    if (!paramOrgId && resolvedId) {
      navigate(`/app/organizations/${resolvedId}/portfolio-timeline`, { replace: true });
    }
  }, [paramOrgId, authOrg?.id, (authOrg as any)?._id, currentOrg?.id, allOrganizations, navigate]);

  // Load projects for picker when orgId changes
  useEffect(() => {
    if (!orgId) return;
    setIsProjectsLoading(true);
    api
      .get<any>(`/organizations/${orgId}/projects`)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        const normalized = list.map((p: any) => ({
          id: String(p._id || p.id),
          name: p.name,
          key: p.key || 'PROJ',
          color: p.color || '#6366f1',
        }));
        setAllProjects(normalized);
      })
      .catch((err) => console.error("Failed to load projects for timeline picker:", err))
      .finally(() => setIsProjectsLoading(false));
  }, [orgId]);

  function reload() {
    if (!orgId) return;
    setIsTimelineLoading(true);
    setError(null);

    const params = selectedProjectIds && selectedProjectIds.length > 0
      ? { projectIds: selectedProjectIds.join(",") }
      : undefined;

    api
      .get<PortfolioTimelineResponse>(`/organizations/${orgId}/portfolio-timeline`, params)
      .then((res) => {
        setData(res.timeline);
        setAccessibleProjectIds(res.accessibleProjectIds || []);
      })
      .catch((err) => {
        console.error("Failed to load portfolio timeline:", err);
        // Show actual error message from backend if available
        const message = err?.response?.data?.message || err?.message || "Failed to load portfolio timeline";
        setError(message);
      })
      .finally(() => setIsTimelineLoading(false));
  }

  useEffect(() => {
    if (orgId) {
      reload();
    }
  }, [orgId, selectedProjectIds]);

  const pickerProjects = useMemo(
    () => allProjects.filter((p) => accessibleProjectIds.length === 0 || accessibleProjectIds.includes(p.id)),
    [allProjects, accessibleProjectIds]
  );

  const effectiveSelection = selectedProjectIds ?? accessibleProjectIds;
  const orderedProjectIds = pickerProjects.map((p) => p.id);
  const sections = useMemo(() => (data ? buildPortfolioSections(data, orderedProjectIds) : []), [data, orderedProjectIds]);

  async function handleReschedule(rowId: string, dates: { startDate: string; dueDate: string }) {
    try {
      const startIso = dates.startDate ? new Date(dates.startDate).toISOString() : null;
      const dueIso = dates.dueDate ? new Date(dates.dueDate).toISOString() : null;
      await api.patch(`/tasks/${rowId}`, {
        startDate: startIso,
        dueDate: dueIso,
      }, { skipErrorToast: true });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "reschedule this task"));
    } finally {
      reload();
    }
  }

  function toggleProject(projectId: string) {
    setSelectedProjectIds((prev) => {
      const base = prev ?? accessibleProjectIds;
      const next = base.includes(projectId) ? base.filter((id) => id !== projectId) : [...base, projectId];
      return next;
    });
  }

  function handleOrgChange(newOrgId: string) {
    navigate(`/app/organizations/${newOrgId}/portfolio-timeline`);
  }

  // If no orgId available yet, show loading spinner while fetching organizations
  if (!orgId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-xs text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Breadcrumbs with Org Switcher */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Link to="/app" className="hover:text-foreground transition-colors">Workspace</Link>
        <span>/</span>
        <div className="relative">
          <button
            onClick={() => setOrgPickerOpen((v) => !v)}
            className="flex items-center gap-1.5 hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted/50"
          >
            <Building2 className="h-3.5 w-3.5" />
            <span className="font-semibold text-foreground truncate max-w-[200px]">{currentOrg?.name || authOrg?.name || 'Organization'}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
          {orgPickerOpen && (
            <div className="absolute left-0 z-30 mt-1.5 w-64 rounded-xl border border-border bg-popover p-2.5 shadow-xl animate-in fade-in zoom-in-95">
              <div className="mb-2 px-1.5 pb-1 border-b border-border/50">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Switch Organization</span>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {allOrganizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => handleOrgChange(org.id)}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors ${
                      org.id === orgId
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {org.name}
                  </button>
                ))}
                {allOrganizations.length === 0 && (
                  <p className="px-2 py-3 text-xs text-muted-foreground text-center">No organizations available.</p>
                )}
              </div>
            </div>
          )}
        </div>
        <span>/</span>
        <span className="font-semibold text-foreground">Portfolio Timeline</span>
      </div>

      {/* Header & Controls Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <Layers className="h-6 w-6 text-primary" />
              Portfolio Timeline
            </h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Epics and tasks across projects on one unified roadmap.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Project / Space Multi-Select Picker */}
          <div className="relative">
            <button
              onClick={() => setProjectPickerOpen((v) => !v)}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50 transition-colors shadow-xs"
            >
              <span>{effectiveSelection.length} project{effectiveSelection.length === 1 ? "" : "s"}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>

            {projectPickerOpen && (
              <div className="absolute right-0 z-30 mt-1.5 w-72 rounded-xl border border-border bg-popover p-2.5 shadow-xl animate-in fade-in zoom-in-95">
                <div className="mb-2 flex items-center justify-between px-1.5 pb-1 border-b border-border/50">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Projects</span>
                  <button
                    className="text-[11px] font-semibold text-primary hover:underline"
                    onClick={() => setSelectedProjectIds(null)}
                  >
                    Select all
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {pickerProjects.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs text-foreground hover:bg-muted/60 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={effectiveSelection.includes(p.id)}
                        onChange={() => toggleProject(p.id)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                      />
                      <span
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-black text-white uppercase shadow-xs"
                        style={{ backgroundColor: colorForProject(p.id, orderedProjectIds) }}
                      >
                        {p.key.slice(0, 2)}
                      </span>
                      <span className="truncate font-medium">{p.name}</span>
                    </label>
                  ))}
                  {pickerProjects.length === 0 && (
                    <p className="px-2 py-3 text-xs text-muted-foreground text-center">No projects available in this organization.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Zoom Controls (Week / Month / Quarter) */}
          <div className="flex rounded-lg border border-border bg-card p-0.5 shadow-xs">
            {(["week", "month", "quarter"] as ZoomLevel[]).map((z) => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={`rounded-md px-3 py-1 text-xs font-semibold capitalize transition-colors ${
                  zoom === z
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                {z}
              </button>
            ))}
          </div>

          {/* Refresh Button */}
          <button
            onClick={reload}
            className="p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-xs"
            title="Refresh Timeline"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin text-primary" : ""}`} />
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button onClick={reload} className="ml-auto text-sm hover:underline">Retry</button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <RefreshCw className="h-4 w-4 animate-spin text-primary" />
          <span>Loading portfolio roadmap...</span>
        </div>
      )}

      {/* Empty State - No projects in organization */}
      {!isLoading && !error && data && data.epics.length === 0 && data.unassigned.length === 0 && (
        <div className={cn(cardClassName(), 'p-8 text-center')}>
          <Layers className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No data to display</h3>
          <p className="text-sm text-muted-foreground mb-4">
            This organization has no projects with tasks, or the selected projects have no issues.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Link to={`/app/organizations/${orgId}/projects`} className="text-sm text-primary hover:underline">
              Manage Projects
            </Link>
            <span className="text-muted-foreground">·</span>
            <button onClick={reload} className="text-sm text-primary hover:underline">
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Timeline Visualization */}
      {data && (data.epics.length > 0 || data.unassigned.length > 0) && (
        <GanttChart
          sections={sections}
          dependencies={data.dependencies}
          zoom={zoom}
          onReschedule={handleReschedule}
          emptyMessage="No epics or tasks found in the selected projects."
        />
      )}
    </div>
  );
}